/**
 * Calculate comprehensive statistics from audit logs for upline IBO filtering
 * @param {Array} auditLogs - Array of audit log records
 * @returns {Object} Statistics object with breakdowns and totals
 */
export const calculateUplineStatistics = (auditLogs) => {
  const stats = {
    totalRevenue: 0,
    totalTicketsSold: 0,
    totalTransactions: 0,
    currency: 'USD',
    
    // Breakdowns
    byEvent: {},           // { eventName: { revenue, tickets, transactions } }
    byTicketType: {},      // { ticketType: { revenue, tickets, transactions } }
    byPaymentMethod: {},   // { method: { revenue, tickets, transactions } }
    byActionType: {},      // { actionType: count }
    byDownline: {},        // { iboNumber: { name, revenue, tickets } }
    
    // Specific counts
    purchases: 0,
    refunds: 0,
    cancellations: 0,
    transfers: 0,
    validations: 0,
    
    // Financial
    refundedAmount: 0,
    netRevenue: 0,         // totalRevenue - refundedAmount
  };
  
  auditLogs.forEach(log => {
    // Parse amount
    const amount = parseFloat(log.amount) || 0;
    const tickets = log.ticketQuantity || 0;
    const actionType = log.actionType || 'UNKNOWN';
    
    // Set currency from first record
    if (log.currency && !stats.currency) {
      stats.currency = log.currency;
    }
    
    // Count by action type
    stats.byActionType[actionType] = (stats.byActionType[actionType] || 0) + 1;
    
    // Handle different action types
    if (actionType === 'PURCHASE') {
      stats.purchases++;
      stats.totalRevenue += amount;
      stats.totalTicketsSold += tickets;
      stats.totalTransactions++;
      
      // Aggregate by event
      if (log.eventName) {
        if (!stats.byEvent[log.eventName]) {
          stats.byEvent[log.eventName] = { revenue: 0, tickets: 0, transactions: 0 };
        }
        stats.byEvent[log.eventName].revenue += amount;
        stats.byEvent[log.eventName].tickets += tickets;
        stats.byEvent[log.eventName].transactions++;
      }
      
      // Aggregate by ticket type
      if (log.ticketType) {
        if (!stats.byTicketType[log.ticketType]) {
          stats.byTicketType[log.ticketType] = { revenue: 0, tickets: 0, transactions: 0 };
        }
        stats.byTicketType[log.ticketType].revenue += amount;
        stats.byTicketType[log.ticketType].tickets += tickets;
        stats.byTicketType[log.ticketType].transactions++;
      }
      
      // Aggregate by payment method
      if (log.paymentMethod) {
        if (!stats.byPaymentMethod[log.paymentMethod]) {
          stats.byPaymentMethod[log.paymentMethod] = { revenue: 0, tickets: 0, transactions: 0 };
        }
        stats.byPaymentMethod[log.paymentMethod].revenue += amount;
        stats.byPaymentMethod[log.paymentMethod].tickets += tickets;
        stats.byPaymentMethod[log.paymentMethod].transactions++;
      }
      
      // Aggregate by downline IBO
      if (log.iboNumber) {
        if (!stats.byDownline[log.iboNumber]) {
          stats.byDownline[log.iboNumber] = { 
            name: log.userName || log.iboNumber, 
            revenue: 0, 
            tickets: 0 
          };
        }
        stats.byDownline[log.iboNumber].revenue += amount;
        stats.byDownline[log.iboNumber].tickets += tickets;
      }
    } else if (actionType === 'REFUND') {
      stats.refunds++;
      stats.refundedAmount += amount;
    } else if (actionType === 'CANCELLATION') {
      stats.cancellations++;
    } else if (actionType.includes('TRANSFER')) {
      stats.transfers++;
    } else if (actionType.includes('VALIDATE')) {
      stats.validations++;
    }
  });
  
  // Calculate net revenue
  stats.netRevenue = stats.totalRevenue - stats.refundedAmount;
  
  // Sort top performers
  stats.topDownlines = Object.entries(stats.byDownline)
    .map(([ibo, data]) => ({ ibo, ...data }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10);
  
  return stats;
};
